import React, { Suspense, FunctionComponent, useMemo, Fragment, useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Router, Redirect, Location } from '@reach/router'
import { Observer } from 'mobx-react-lite'
import { Global } from '@emotion/core'

import { globalStyles } from './styles'
import { Spinner, ErrorBoundary, Navbar } from './components'
import { UserStore, ApiStore, NotificationsStore } from './stores'

const LandingScreen = React.lazy(() =>
  import('./screens/landing').then((t) => ({ default: t.LandingScreen })),
)
const AuthScreen = React.lazy(() =>
  import('./screens/auth').then((t) => ({ default: t.AuthScreen })),
)
const RequestsScreen = React.lazy(() =>
  import('./screens/requests').then((t) => ({ default: t.RequestsScreen })),
)
const MyRequestsScreen = React.lazy(() =>
  import('./screens/my-requests').then((t) => ({ default: t.MyRequestsScreen })),
)
const RequestScreen = React.lazy(() =>
  import('./screens/request').then((t) => ({ default: t.RequestScreen })),
)
const NewRequestScreen = React.lazy(() =>
  import('./screens/new-request').then((t) => ({ default: t.NewRequestScreen })),
)
const MyOffersScreen = React.lazy(() =>
  import('./screens/my-offers').then((t) => ({ default: t.MyOffersScreen })),
)
const ProfileScreen = React.lazy(() =>
  import('./screens/profile').then((t) => ({ default: t.ProfileScreen })),
)
const WhyScreen = React.lazy(() =>
  import('./screens/why').then((t) => ({ default: t.WhyScreen })),
)

export const App: FunctionComponent = () => {
  const userStore = useMemo(() => new UserStore(), [])
  const apiStore = useMemo(() => new ApiStore(userStore), [])
  const notificationsStore = useMemo(
    () => new NotificationsStore(userStore, apiStore),
    [],
  )

  return (
    <ErrorBoundary id="root">
      <Global styles={globalStyles} />
      <HelmetProvider>
        <Observer>
          {() => {
            useEffect(() => {
              if (userStore.ready && userStore.authenticated) {
                // get notifications
                notificationsStore.getNotifications()

                // get profile
                userStore.getUserProfile().then(() => {
                  // inherit email from attributes if not set
                  if (!userStore.userProfile.contacts?.Email) {
                    userStore.setUserProfile({
                      ...userStore.userProfile,
                      contacts: {
                        ...userStore.userProfile.contacts,
                        Email: userStore.userAttributesObject.email,
                      },
                    })
                  }
                })
              }
            }, [userStore.ready && userStore.authenticated])

            // still loading
            if (!userStore.ready) return <Spinner />

            // not authenticated
            if (!userStore.authenticated)
              return (
                <Suspense fallback={<Spinner />}>
                  <Router>
                    <LandingScreen path="/" />
                    <AuthScreen path="/create-account" mode="create-account" />
                    <AuthScreen path="/sign-in" mode="sign-in" />
                    <WhyScreen path="/why" showHeader />
                    <Redirect from="*" to="/" noThrow />
                  </Router>
                </Suspense>
              )

            // authenticated
            return (
              <Fragment>
                <Navbar userStore={userStore} notificationsStore={notificationsStore} />

                <Suspense fallback={<Spinner />}>
                  <Router>
                    <RequestsScreen
                      path="/requests"
                      userStore={userStore}
                      apiStore={apiStore}
                    />
                    <NewRequestScreen
                      path="/requests/new"
                      userStore={userStore}
                      apiStore={apiStore}
                    />
                    <RequestScreen
                      path="/requests/:requestID"
                      userStore={userStore}
                      apiStore={apiStore}
                    />
                    <MyRequestsScreen
                      path="/requests/my"
                      userStore={userStore}
                      apiStore={apiStore}
                    />
                    <MyOffersScreen
                      path="/offers/my"
                      userStore={userStore}
                      apiStore={apiStore}
                    />
                    <ProfileScreen
                      path="/profile"
                      userStore={userStore}
                      apiStore={apiStore}
                    />
                    <WhyScreen path="/why" />
                    <Redirect from="*" to="/requests" noThrow />
                  </Router>
                </Suspense>
              </Fragment>
            )
          }}
        </Observer>
      </HelmetProvider>
    </ErrorBoundary>
  )
}
